import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  StaffLeaveManagementService,
  LeaveClassGroup,
  LeaveRequestItem,
  LeaveStatusCounts,
  LeaveFilterOption,
  LeaveDisplayStatus,
  LeaveAttachment,
} from '../services/staff-leave-management.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;
const ALL = 'All';

type StatusFilterKey = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

@Component({
  selector: 'app-staff-leave-management',
  templateUrl: './staff-leave-management.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-leave-management.component.css']
})
export class StaffLeaveManagementComponent implements OnInit {
  // ---------- List state ----------
  classes: LeaveClassGroup[] = [];
  statusCounts: LeaveStatusCounts = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };

  pageNumber = 1;
  maxPageNumber = 1;
  fetchingData = true;
  searching = false;
  applyingFilter = false;
  loadingMore = false;

  readonly skeletonRows = Array.from({ length: 6 }, () => ({}));

  get isLoading(): boolean {
    return this.fetchingData || this.searching || this.applyingFilter;
  }

  get hasAnyLeaveRequests(): boolean {
    return this.classes.some(c => c.leaveRequests.length > 0);
  }

  // ---------- Search ----------
  searchOpen = false;
  searchString = '';
  private searchDebounceTimer: any;
  private isFirstLoad = true;

  // ---------- Status filter chips ----------
  activeStatus: StatusFilterKey = 'ALL';

  get allCount(): number {
    return this.statusCounts.pending + this.statusCounts.approved + this.statusCounts.rejected + this.statusCounts.cancelled;
  }

  selectStatus(status: StatusFilterKey) {
    if (this.activeStatus === status) { return; }
    this.activeStatus = status;
    this.applyFiltersAndRefetch();
  }

  // ---------- Applied class/date filters ----------
  selectedClassCategory = ALL;
  selectedClassStandard = ALL;
  selectedClassId = ALL;
  selectedStartDate: string | null = null;
  selectedEndDate: string | null = null;

  // ---------- Filter sheet (draft state) ----------
  showFilterSheet = false;
  draftClassCategory = ALL;
  draftClassStandard = ALL;
  draftClassId = ALL;
  draftStartDateObj: Date | null = null;
  draftEndDateObj: Date | null = null;
  activeDateField: 'start' | 'end' | null = null;

  classCategoryOptions: LeaveFilterOption[] = [];
  classStandardOptions: LeaveFilterOption[] = [];
  classSectionOptions: LeaveFilterOption[] = [];
  loadingClassCategories = false;
  loadingClassStandards = false;
  loadingClassSections = false;

  get isDateRangeIncomplete(): boolean {
    return !!this.draftStartDateObj && !this.draftEndDateObj;
  }

  get hasActiveFilters(): boolean {
    return this.selectedClassCategory !== ALL
      || this.selectedClassStandard !== ALL
      || this.selectedClassId !== ALL
      || !!this.selectedStartDate
      || !!this.selectedEndDate;
  }

  // ---------- Detail drawer ----------
  showDetails = false;
  selectedLeave: LeaveRequestItem | null = null;
  actioning = false;

  // ---------- Remark modal ----------
  showRemarkModal = false;
  remarkModalAction: 'APPROVE' | 'REJECT' | null = null;
  remarkText = '';

  constructor(
    private staffLeaveService: StaffLeaveManagementService,
    private staffSession: StaffSessionService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadList(1);
  }

  // ---------- List loading ----------

  async loadList(page: number) {
    try {
      const response = await firstValueFrom(this.staffLeaveService.getLeaveManagementList({
        status: this.activeStatus,
        startDate: this.selectedStartDate || '',
        endDate: this.selectedEndDate || '',
        studentName: this.searchString,
        classIds: this.selectedClassId === ALL ? '' : this.selectedClassId,
        classCategoryIds: this.selectedClassCategory === ALL ? '' : this.selectedClassCategory,
        classStandardIds: this.selectedClassStandard === ALL ? '' : this.selectedClassStandard,
        page,
        limit: PAGE_LIMIT,
      }));
      if (response?.status && response.body) {
        const newClasses = response.body.classes || [];
        this.classes = page === 1 ? newClasses : this.mergeClassGroups(this.classes, newClasses);
        this.statusCounts = response.body.statusCounts || { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
        this.maxPageNumber = response.body.maxPageNumber || 1;
        this.pageNumber = page;
      } else {
        this.classes = [];
        const message = await firstValueFrom(this.translate.get('couldNotLoadLeaveRequestsTryAgain'));
        customToastr(message, 'error');
      }
    } catch {
      this.classes = [];
      const message = await firstValueFrom(this.translate.get('couldNotLoadLeaveRequestsTryAgain'));
      customToastr(message, 'error');
    } finally {
      this.fetchingData = false;
      this.searching = false;
      this.applyingFilter = false;
      this.loadingMore = false;
      this.isFirstLoad = false;
    }
  }

  // New pages merge per class group by classId instead of a flat append —
  // a class already shown on an earlier page gets its new leave requests
  // appended into that same section rather than creating a duplicate
  // class header (per spec's mergeSections()).
  private mergeClassGroups(existing: LeaveClassGroup[], incoming: LeaveClassGroup[]): LeaveClassGroup[] {
    const merged = existing.map(c => ({ ...c, leaveRequests: [...c.leaveRequests] }));
    for (const group of incoming) {
      const match = merged.find(c => c.classId === group.classId);
      if (match) {
        match.leaveRequests.push(...group.leaveRequests);
      } else {
        merged.push({ ...group, leaveRequests: [...group.leaveRequests] });
      }
    }
    return merged;
  }

  loadMore() {
    if (this.pageNumber >= this.maxPageNumber) { return; }
    if (this.isLoading || this.loadingMore) { return; }
    this.loadingMore = true;
    this.loadList(this.pageNumber + 1);
  }

  refreshList() {
    this.classes = [];
    this.fetchingData = true;
    this.loadList(1);
  }

  // ---------- Search ----------

  toggleSearch() {
    this.searchOpen = !this.searchOpen;
    if (!this.searchOpen) {
      this.searchString = '';
      this.applyFiltersAndRefetch();
    }
  }

  onSearchInput(value: string) {
    this.searchString = value;
    if (this.isFirstLoad) { return; }
    this.searching = true;
    this.classes = [];
    this.pageNumber = 1;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadList(1), SEARCH_DEBOUNCE_MS);
  }

  private applyFiltersAndRefetch() {
    this.applyingFilter = true;
    this.classes = [];
    this.pageNumber = 1;
    this.loadList(1);
  }

  // ---------- Filter sheet ----------

  async openFilterSheet() {
    this.draftClassCategory = this.selectedClassCategory;
    this.draftClassStandard = this.selectedClassStandard;
    this.draftClassId = this.selectedClassId;
    this.draftStartDateObj = this.selectedStartDate ? moment(this.selectedStartDate).toDate() : null;
    this.draftEndDateObj = this.selectedEndDate ? moment(this.selectedEndDate).toDate() : null;
    this.activeDateField = null;
    this.classStandardOptions = [];
    this.classSectionOptions = [];
    this.showFilterSheet = true;
    await this.loadClassCategoryOptions();
  }

  closeFilterSheet() {
    this.showFilterSheet = false;
    this.activeDateField = null;
  }

  private async loadClassCategoryOptions() {
    this.loadingClassCategories = true;
    try {
      const response = await firstValueFrom(this.staffLeaveService.getClassCategoryOptions());
      this.classCategoryOptions = response?.status ? (response.body?.classCategoryListData || []) : [];
    } catch {
      this.classCategoryOptions = [];
    } finally {
      this.loadingClassCategories = false;
    }
  }

  async onDraftClassCategoryChange(category: string) {
    this.draftClassCategory = category;
    this.draftClassStandard = ALL;
    this.draftClassId = ALL;
    this.classStandardOptions = [];
    this.classSectionOptions = [];
    if (category === ALL) { return; }
    this.loadingClassStandards = true;
    try {
      const response = await firstValueFrom(this.staffLeaveService.getClassStandardOptions(category));
      this.classStandardOptions = response?.status
        ? this.staffLeaveService.flattenGroupedList(response.body?.classStandardList || [], 'classStandardList')
        : [];
    } catch {
      this.classStandardOptions = [];
    } finally {
      this.loadingClassStandards = false;
    }
  }

  async onDraftClassStandardChange(classStandard: string) {
    this.draftClassStandard = classStandard;
    this.draftClassId = ALL;
    this.classSectionOptions = [];
    if (classStandard === ALL) { return; }
    this.loadingClassSections = true;
    try {
      const response = await firstValueFrom(
        this.staffLeaveService.getClassSectionOptions(this.draftClassCategory, classStandard)
      );
      const groups = response?.status ? (response.body?.classSectionList || []) : [];
      // Sections nest two levels deep (groups -> standards -> sections) —
      // flatten once for standards, again for the sections inside each.
      const standardGroups = this.staffLeaveService.flattenGroupedList(groups, 'classStandardList') as any[];
      this.classSectionOptions = standardGroups.length
        ? this.staffLeaveService.flattenGroupedList(standardGroups, 'classSectionList')
        : this.staffLeaveService.flattenGroupedList(groups, 'classSectionList');
    } catch {
      this.classSectionOptions = [];
    } finally {
      this.loadingClassSections = false;
    }
  }

  onDraftClassIdChange(classId: string) {
    this.draftClassId = classId;
  }

  openDateField(field: 'start' | 'end') {
    if (field === 'end' && !this.draftStartDateObj) { return; }
    this.activeDateField = field;
  }

  closeDatePicker() {
    this.activeDateField = null;
  }

  onDraftDateSelected(date: Date) {
    if (this.activeDateField === 'start') {
      this.draftStartDateObj = date;
      // If the new start date falls after the current end date, the end
      // date is silently cleared — you can't have an inverted range.
      if (this.draftEndDateObj && moment(date).isAfter(moment(this.draftEndDateObj), 'day')) {
        this.draftEndDateObj = null;
      }
    } else if (this.activeDateField === 'end') {
      this.draftEndDateObj = date;
    }
    this.activeDateField = null;
  }

  clearFilter() {
    if (!this.hasActiveFilters) { return; }
    this.selectedClassCategory = ALL;
    this.selectedClassStandard = ALL;
    this.selectedClassId = ALL;
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.showFilterSheet = false;
    this.applyFiltersAndRefetch();
  }

  applyFilter() {
    if (this.isDateRangeIncomplete) { return; }
    this.selectedClassCategory = this.draftClassCategory;
    this.selectedClassStandard = this.draftClassStandard;
    this.selectedClassId = this.draftClassId;
    this.selectedStartDate = this.draftStartDateObj ? moment(this.draftStartDateObj).format('YYYY-MM-DD') : null;
    this.selectedEndDate = this.draftEndDateObj ? moment(this.draftEndDateObj).format('YYYY-MM-DD') : null;
    this.showFilterSheet = false;
    this.applyFiltersAndRefetch();
  }

  // ---------- Detail drawer ----------

  openLeaveDetails(item: LeaveRequestItem) {
    this.selectedLeave = item;
    this.showDetails = true;
  }

  closeDetails() {
    if (this.isAttachmentPreviewOpen) {
      // Back-button/close priority: the attachment preview, then the
      // remark modal, then finally the detail drawer — never more than
      // one layer closes per tap, matching the mobile app's hardware-back
      // stack.
      this.closeAttachmentPreview();
      return;
    }
    if (this.showRemarkModal) {
      this.closeRemarkModal();
      return;
    }
    this.showDetails = false;
    this.selectedLeave = null;
  }

  get isHalfDayLeave(): boolean {
    return !!(this.selectedLeave?.startTime && this.selectedLeave?.endTime);
  }

  // ---------- Approve / Reject ----------

  openRemarkModal(action: 'APPROVE' | 'REJECT') {
    this.remarkModalAction = action;
    this.remarkText = '';
    this.showRemarkModal = true;
  }

  closeRemarkModal() {
    this.showRemarkModal = false;
    this.remarkModalAction = null;
    this.remarkText = '';
  }

  async confirmAction() {
    if (!this.selectedLeave || !this.remarkModalAction) { return; }
    // Rejection requires a remark; approval doesn't.
    if (this.remarkModalAction === 'REJECT' && !this.remarkText.trim()) {
      const message = await firstValueFrom(this.translate.get('rejectionReasonRequired'));
      customToastr(message, 'error');
      return;
    }
    this.actioning = true;
    try {
      const response = await firstValueFrom(this.staffLeaveService.actionLeaveRequest({
        leaveRequestId: this.selectedLeave.leaveRequestId,
        action: this.remarkModalAction,
        remarks: this.remarkText.trim() || null,
      }));
      if (response?.status) {
        // Optimistic in-place update — no full refetch. Updates both the
        // list item and the detail drawer's own selectedLeave reference so
        // the sheet reflects the new state immediately without closing.
        const newStatus: LeaveDisplayStatus = this.remarkModalAction === 'APPROVE' ? 'Approved' : 'Rejected';
        const staffDetails = this.staffSession.getStaffDetails();
        this.updateLeaveInPlace(this.selectedLeave.leaveRequestId, {
          displayStatus: newStatus,
          actionedByName: staffDetails?.staffName || null,
          actionedByImage: staffDetails?.staffImage || null,
          remarks: this.remarkText.trim() || null,
        });
        this.closeRemarkModal();
      } else {
        const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
        customToastr(message, 'error');
      }
    } catch {
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.actioning = false;
    }
  }

  private updateLeaveInPlace(leaveRequestId: string, changes: Partial<LeaveRequestItem>) {
    for (const group of this.classes) {
      const item = group.leaveRequests.find(r => r.leaveRequestId === leaveRequestId);
      if (item) {
        Object.assign(item, changes);
        break;
      }
    }
    if (this.selectedLeave?.leaveRequestId === leaveRequestId) {
      this.selectedLeave = { ...this.selectedLeave, ...changes };
    }
  }

  // ---------- Attachment preview (in-app, same pattern as broadcast's
  // media preview overlay) — images render directly, everything else
  // (PDFs/documents) renders in a sandboxed iframe via a sanitized URL,
  // rather than opening the attachment in a new browser tab. ----------
  isAttachmentPreviewOpen = false;
  previewAttachment: LeaveAttachment | null = null;
  previewAttachmentSafeUrl: SafeResourceUrl | null = null;

  // "Attachment (1)" for a single file, "Attachments (n)" for more than
  // one — not a static plural label with a count tacked on.
  attachmentsSectionLabel(count: number): string {
    const key = count === 1 ? 'attachment' : 'attachments';
    return this.translate.instant(key) + ` (${count})`;
  }

  attachmentUrl(attachment: LeaveAttachment): string {
    return attachment?.url || attachment?.fileUrl || attachment?.attachmentUrl || attachment?.documentUrl || '';
  }

  attachmentName(attachment: LeaveAttachment, index: number): string {
    return attachment?.name || attachment?.fileName || attachment?.attachmentName
      || `${this.translate.instant('attachment')} ${index + 1}`;
  }

  isImageAttachment(attachment: LeaveAttachment): boolean {
    const type = (attachment?.type || attachment?.fileType || attachment?.attachmentType || '').toString().toUpperCase();
    if (type) { return type === 'IMAGE' || ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(type); }
    const url = this.attachmentUrl(attachment).toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp)(\?|$)/.test(url);
  }

  openAttachmentPreview(attachment: LeaveAttachment) {
    const url = this.attachmentUrl(attachment);
    if (!url) { return; }
    this.previewAttachment = attachment;
    this.previewAttachmentSafeUrl = this.isImageAttachment(attachment)
      ? null
      : this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.isAttachmentPreviewOpen = true;
  }

  closeAttachmentPreview() {
    this.isAttachmentPreviewOpen = false;
    this.previewAttachment = null;
    this.previewAttachmentSafeUrl = null;
  }

  // ---------- Display helpers ----------

  statusChipClass(status: LeaveDisplayStatus): string {
    switch (status) {
      case 'Approved': return 'st-chip-green';
      case 'Pending': return 'st-chip-yellow';
      case 'Rejected': return 'st-chip-red';
      default: return 'st-chip-grey';
    }
  }

  getInitials(name: string): string {
    return this.staffSession.getInitials(name);
  }

  private formatDate(date: string): string {
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : date;
  }

  // Collapses to a single DD/MM/YYYY date when start and end are the same
  // day, instead of showing "31/07/2026 - 31/07/2026".
  dateRangeLabel(item: LeaveRequestItem): string {
    if (item.startDate === item.endDate) {
      return this.formatDate(item.startDate);
    }
    return `${this.formatDate(item.startDate)} - ${this.formatDate(item.endDate)}`;
  }
}
